import React, { createContext, useState } from 'react'
import api from '../services/api'
import { useSocket } from './socketProvider'

type LocationProps = {
  id: string
  accuracy: string
  altitude: string
  latitude: number
  longitude: number
}

type VehicleProps = {
  id: string
  vehicle: string
  color: string
  plate: string
  type: string
}

type BaseProps = {
  id: string
  base: string
  outpost: string
}

type OccurrenceProps = {
  Resident: {
    id_user: string
    name: string
    phone: string
    Base: BaseProps
    Vehicle: VehicleProps[]
  }
  Employee: unknown[]
  outpost: string
  occurrence_id: string
  occurrence_name: string
  occurrence_code: string
  occurrence_type: string
  occurrence_status: string
  additional_info: string
  location: LocationProps[]
  created_at: string
}

type CustomersProps = {
  id: string
  value: string
}

type SecuritiesProps = {
  id: string
  Userinfo: {
    id: string
    name: string
    email: string
    phone: string
    Vehicle: {
      id: string
      vehicle: string
      color: string
      plate: string
      type: string
    }[]
  }
  Employee: {
    id: string
    status: string
  }
  AlwaysAlert: string
  UserDistance: {
    distance: {
      text: string
    }
    duration: {
      text: string
    }
  }
  OccurrenceDistance: {
    distance: {
      text: string
    }
  }
  Location: null
  online: string
  status: string
}

type ContextProps = {
  occurrencesList: OccurrenceProps[] | null
  customersList: CustomersProps | null
  // filters: unknown
  handleFilters: (field: string, value: any) => void
  getAllOccurrences: () => void
  getCustomersList: () => void
  callFilteredOccurrences: (filters: any) => void
  getSecuritiesList: (occurrenceId: string, baseId: string) => void
  securitiesList: SecuritiesProps[] | null
  triggerSecurity: (occurrence: OccurrenceProps, idEmployee: string) => void
  triggerSecuritySuccess: boolean
  setOccurrencesList: (occurrences: OccurrenceProps[] | null) => void
  filter: {
    customer: string
    start_date: string
    end_date: string
    start_hour: string
    end_hour: string
    status: string
  }
  filteredData: {
    customer: string
    start_date: string
    end_date: string
    start_hour: string
    end_hour: string
    status: string
  }
  setFilter: React.Dispatch<React.SetStateAction<{
    customer: string
    start_date: string
    end_date: string
    start_hour: string
    end_hour: string
    status: string
  }>>
  setFilteredData: React.Dispatch<React.SetStateAction<{
    customer: string
    start_date: string
    end_date: string
    start_hour: string
    end_hour: string
    status: string
  }>>
  setOccurrencesList: React.Dispatch<React.SetStateAction<OccurrenceProps[] | null>>
}

export const ContextLatestRequests = createContext<ContextProps | undefined>(
  undefined,
)

const ContextLatestRequestsProvider = (props) => {
  const { socket } = useSocket()

  const [occurrencesList, setOccurrencesList] =
    useState<OccurrenceProps[] | null>(null)
  const [customersList, setCustomersList] = useState<CustomersProps | null>(
    null,
  )
  const [securitiesList, setSecuritiesList] = useState<SecuritiesProps[] | null>(
    null,
  )
  const [filteredData, setFilteredData] = useState({
    customer: '',
    start_date: '',
    end_date: '',
    start_hour: '',
    end_hour: '23:59',
    status: '',
  })

  const [filter, setFilter] = useState({
    customer: '',
    start_date: '',
    end_date: '',
    start_hour: '',
    end_hour: '23:59',
    status: '',
  })

  const [triggerSecuritySuccess, setTriggerSecuritySuccess] = useState(false)

  const handleFilters = (field: string, value: any) => {
    // Handle date objects by converting them to ISO strings
    let processedValue = value
    if (value instanceof Date) {
      processedValue = value.toISOString()
    }
    
    setFilter((prevItem) => ({
      ...prevItem,
      [field]: processedValue,
    }))
  }

  const callFilteredOccurrences = (filters: any) => {
    // Clean up empty filter values
    if (filters?.status === '') delete filters.status
    if (filters?.customer === '') delete filters.customer
    if (filters?.start_date === '') delete filters.start_date
    if (filters?.end_date === '') delete filters.end_date
    if (filters?.start_hour === '') delete filters.start_hour
    if (filters?.end_hour === '') delete filters.end_hour
    
    api
      .post('/v1/dashboard/surveillance/occurrences', filters)
      .then((response) => {
        console.log(response.data)
        setOccurrencesList(response.data)
      })
      .catch((error) => {
        console.log(error.response.data.statusCode)
      })
  }

  const getSecuritiesList = (occurrenceId, idBase) => {
    console.log(occurrenceId, idBase)
    api
      .post('/v1/dashboard/employees/online', {
        id_base: idBase,
        id_occurrence: occurrenceId,
      })
      .then((response) => {
        console.log(response.data)
        const data = response.data
        const availableSecurity = data.filter(
          (el) => el.Employee.status !== 'OCORRENCIA',
        )
        setSecuritiesList(availableSecurity)
      })
      .catch((error) => {
        console.log(error.response.data.statusCode)
      })
  }

  const getCustomersList = () => {
    api
      .post('/v1/dashboard/surveillance/occurrences', {})
      .then((response) => {
        const res = response.data
        const list = res.map((item) => {
          const id = item.Resident.id_user
          const value = item.Resident.name

          return { id, value }
        })
        const listSet = [...new Set(list)]
        setCustomersList(listSet)
      })
      .catch((error) => {
        console.log(error.response.data.statusCode)
      })
  }

  const getAllOccurrences = () => {
    api
      .post('/v1/dashboard/surveillance/occurrences', {})
      .then((response) => {
        setOccurrencesList(response.data)
      })
      .catch((error) => {
        console.log(error.response.data.statusCode)
      })
  }

  const triggerSecurity = (occurrence, idEmployee) => {
    const payload = {
      id_occurrence: occurrence.Occurrence.id,
      id_employee: idEmployee,
    }
    console.log(payload)

    socket.emit('occurrences:notification', payload)
    setTriggerSecuritySuccess(true)
    // api
    //   .put('/v1/occurrences/update', dataJson)
    //   .then((res) => {
    //     console.log(res)
    //     setTriggerSecuritySuccess(true)
    //   })
    //   .catch((err) => {
    //     console.log(err)
    //     setTriggerSecuritySuccess(false)
    //   })
  }

  const contextValue: ContextProps = {
    occurrencesList,
    customersList,
    handleFilters,
    getAllOccurrences,
    getCustomersList,
    callFilteredOccurrences,
    setOccurrencesList,
    getSecuritiesList,
    securitiesList,
    triggerSecurity,
    triggerSecuritySuccess,
    filter,
    filteredData,
    setFilter,
    setFilteredData,
  }

  return (
    <ContextLatestRequests.Provider value={contextValue}>
      {props.children}
    </ContextLatestRequests.Provider>
  )
}

export default ContextLatestRequestsProvider
